const RESET_PASSWORD_PATH = "/reset-password"
const RESET_LINK_INVALID_MESSAGE =
  "This reset link is invalid or has expired. Please request a new one."

export type AuthCallbackDecision =
  | {
      action: "redirect"
      path: string
    }
  | {
      action: "implicit"
      accessToken: string
      refreshToken: string
      next: string
    }
  | {
      action: "error"
      message: string
    }

export function isSafeNextPath(next: string): boolean {
  return next.startsWith("/") && !next.startsWith("//") && !next.includes("://")
}

export function safeAuthCallbackNext(
  next: string | null,
  type: string | null
): string {
  const fallback = type === "recovery" ? RESET_PASSWORD_PATH : "/"
  if (!next || !isSafeNextPath(next)) return fallback
  if (type === "recovery" && next === "/") return RESET_PASSWORD_PATH
  return next
}

export function isPasswordRecoveryCallback(
  type: string | null,
  next: string
): boolean {
  return type === "recovery" || next === RESET_PASSWORD_PATH
}

export function getPasswordResetEmailRedirectTo(origin: string): string {
  const url = new URL("/api/auth/callback", origin)
  url.searchParams.set("next", RESET_PASSWORD_PATH)
  url.searchParams.set("type", "recovery")
  return url.toString()
}

export function getRecoveryLinkErrorMessage(
  error: string | null,
  errorDescription: string | null
): string {
  const combined = `${error ?? ""} ${errorDescription ?? ""}`
    .toLowerCase()
    .replace(/\+/g, " ")
  if (
    combined.includes("otp_expired") ||
    combined.includes("expired") ||
    combined.includes("access_denied") ||
    combined.includes("invalid")
  ) {
    return RESET_LINK_INVALID_MESSAGE
  }
  if (errorDescription) {
    try {
      return decodeURIComponent(errorDescription.replace(/\+/g, " "))
    } catch {
      return RESET_LINK_INVALID_MESSAGE
    }
  }
  return RESET_LINK_INVALID_MESSAGE
}

function readParams(raw: string): URLSearchParams {
  return new URLSearchParams(raw.replace(/&amp;/g, "&"))
}

export function parseAuthCallbackUrl(
  href: string,
  options?: { defaultNext?: string }
): AuthCallbackDecision {
  const url = new URL(href, "https://pleasur.ai")
  const search = url.searchParams
  const hash = readParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash)

  const code = search.get("code") ?? hash.get("code")
  const tokenHash = search.get("token_hash") ?? hash.get("token_hash")
  const type = search.get("type") ?? hash.get("type")
  const accessToken = hash.get("access_token")
  const refreshToken = hash.get("refresh_token")
  const error = search.get("error") ?? hash.get("error")
  const errorCode = search.get("error_code") ?? hash.get("error_code")
  const errorDescription =
    search.get("error_description") ?? hash.get("error_description")

  const requestedNext = search.get("next") ?? hash.get("next") ?? options?.defaultNext ?? null
  const next = safeAuthCallbackNext(
    requestedNext,
    type ?? (options?.defaultNext === RESET_PASSWORD_PATH ? "recovery" : null)
  )

  if (error || errorCode) {
    return {
      action: "error",
      message: getRecoveryLinkErrorMessage(error ?? errorCode, errorDescription),
    }
  }

  if (tokenHash && type) {
    const params = new URLSearchParams({
      token_hash: tokenHash,
      type,
      next,
    })
    return { action: "redirect", path: `/api/auth/callback?${params.toString()}` }
  }

  if (code) {
    const params = new URLSearchParams({ code, next })
    if (type) params.set("type", type)
    return { action: "redirect", path: `/api/auth/callback?${params.toString()}` }
  }

  if (accessToken && refreshToken) {
    return {
      action: "implicit",
      accessToken,
      refreshToken,
      next: type === "recovery" ? RESET_PASSWORD_PATH : next,
    }
  }

  return {
    action: "error",
    message: RESET_LINK_INVALID_MESSAGE,
  }
}
